import { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material";
import useSearchValues from "@navigation/SearchModal/useSearchValues";
import { TopBarContainer as SearchModalTopBarContainer } from "@navigation/TopNavigation/TopBar/TopBar";
import { CrossIcon, LeftArrowIcon, SearchIcon } from "@common/Icons";
import { maxPageContentWidth, searchInputPlaceholderText } from "@constants/consts";
import { colors, fonts } from "@constants/cssVariables";
import updateSearchParams from "@utils/navigation/updateSearchParams";

export const backIconAriaLabel = "back";
export const searchInputAriaLabel = "search";
export const clearInputIconAriaLabel = "clear";

export const SearchModalTopBarContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  height: "100%",
  maxWidth: maxPageContentWidth,
  margin: "0 auto",

  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(0, 8),
  },

  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(0, 24),
  },
}));

const SearchModalBackIcon = styled(LeftArrowIcon)({
  cursor: "pointer",
  svg: {
    display: "block",
    color: colors.neutral0,
    width: 32,
    height: 32,
  },
});

const SearchModalInputContainer = styled("div")(({ theme }) => ({
  cursor: "text",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  height: 40,
  borderRadius: 100,
  padding: theme.spacing(2, 4, 2, 3),
  gap: theme.spacing(3),
  backgroundColor: "rgba(255,255,255, 0.2)", // We use rgba format here in order to apply opacity exclusively to the background color
  overflow: "hidden",
}));

const SearchModalSearchIcon = styled(SearchIcon)(() => ({
  cursor: "text",
  svg: {
    display: "block",
    color: colors.neutral0,
    width: 24,
    height: 24,
  },
}));

const SearchModalInput = styled("input")(() => ({
  display: "flex",
  flex: 1,
  backgroundColor: "transparent",
  color: colors.neutral0,
  font: fonts.bodyMedium,
  border: "none",
  outline: "none",
  overflow: "hidden",

  "::placeholder": {
    color: colors.neutral0,
    opacity: 1, // Firefox adds a lower opacity to the placeholder
  },
}));

const SearchModalClearInputIcon = styled(CrossIcon)(() => ({
  cursor: "pointer",
  svg: {
    display: "block",
    color: colors.neutral0,
    width: 16,
    height: 16,
  },
}));

const SearchModalTopBar: React.FC = () => {
  const { searchParam } = useSearchValues();
  const searchModalInputRef = useRef<HTMLInputElement>(null);
  const [inputText, setInputText] = useState(searchParam);
  const debounceTimer = useRef<NodeJS.Timeout | number | null>(null);

  useEffect(() => {
    return () => {
      // Cleanup debounce timer
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [debounceTimer]);

  const handleCloseSearchModal = () => {
    updateSearchParams(null);
  };

  const handleSearchModalInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const { value } = event.target;
    setInputText(value);
    if (value.length >= 3) debounceTimer.current = setTimeout(updateSearchParams, 500, value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === "Enter" || event.code === "NumpadEnter") {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      updateSearchParams(inputText);
    }
  };

  const handleSearchReset = () => {
    setInputText("");
    updateSearchParams("");
    searchModalInputRef.current?.blur();
  };

  return (
    <SearchModalTopBarContainer>
      <SearchModalTopBarContentWrapper>
        <SearchModalBackIcon onClick={handleCloseSearchModal} role="button" aria-label={backIconAriaLabel} />
        <SearchModalInputContainer>
          <SearchModalSearchIcon />
          <SearchModalInput
            ref={searchModalInputRef}
            aria-label={searchInputAriaLabel}
            placeholder={searchInputPlaceholderText}
            value={inputText}
            onChange={handleSearchModalInputChange}
            onKeyDown={handleKeyDown}
            spellCheck={false}
            autoFocus
          />
          {inputText && (
            <SearchModalClearInputIcon onClick={handleSearchReset} role="button" aria-label={clearInputIconAriaLabel} />
          )}
        </SearchModalInputContainer>
      </SearchModalTopBarContentWrapper>
    </SearchModalTopBarContainer>
  );
};

export default SearchModalTopBar;
